import React, { useEffect, useState } from 'react'
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Select,
  Upload,
  Divider,
  Space,
  DatePicker,
  Table,
  Modal,
  Spin,
} from 'antd'
import {
  UploadOutlined,
  EditOutlined,
  PlusOutlined,
  DeleteOutlined,
  EyeOutlined,
} from '@ant-design/icons'
import { toast } from 'react-toastify'
import dayjs from 'dayjs'

import AdminHeader from '../CommonComponents/AdminHeader'
import AdminFooter from '../CommonComponents/AdminFooter'
import {
  DeleteMedicalDocuments,
  getChildrenList,
  GetMedicalDocuments,
  GetMedicalDocumentsById,
  IMG_URL,
  saveMedicalDocuments,
  UpdateMedicalDocuments,
} from '../APIComponents/AdminApi'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const MedicalDocumentsForm = () => {
  //===============================================
  const loginType = sessionStorage.getItem('loginType') // 'ADMIN' or 'USER'
  const menuData = JSON.parse(sessionStorage.getItem('menus')) || []

  const getPagePermission = () => {
    if (loginType === 'ADMIN') {
      return {
        is_new: 1,
        is_edit: 1,
        is_view: 1,
        is_delete: 1,
      }
    }

    for (const menu of menuData) {
      const page = menu.pages?.find((p) => p.page_name === 'Medical Documents')
      if (page) return page
    }

    return {
      is_new: 0,
      is_edit: 0,
      is_view: 0,
      is_delete: 0,
    }
  }

  const pagePermission = getPagePermission()
  const showActionColumn =
    loginType === 'ADMIN' ||
    pagePermission.is_edit == 1 ||
    pagePermission.is_view == 1 ||
    pagePermission.is_delete == 1
  //============================================

  const [form] = Form.useForm()

  const [childrenList, setChildrenList] = useState([])
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  const [showForm, setShowForm] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [recordId, setRecordId] = useState(null)

  const [searchText, setSearchText] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [deleteModal, setDeleteModal] = useState(false)
  const [deleteId, setDeleteId] = useState(null)
  const [editLoading, setEditLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [viewModalVisible, setViewModalVisible] = useState(false)
  const [viewData, setViewData] = useState(null)
  const [viewLoading, setViewLoading] = useState(false)

  const handleView = (record) => {
    setViewData(record)
    setViewModalVisible(true)
  }

  useEffect(() => {
    fetchChildren()
  }, [])

  useEffect(() => {
    if (childrenList.length > 0) {
      fetchTableData()
    }
  }, [childrenList])

  // ================= FETCH CHILDREN =================
  const fetchChildren = async () => {
    try {
      const res = await axiosInstance.get(getChildrenList)
      setChildrenList(res.data)
    } catch {
      toast.error('Failed to fetch children list')
    }
  }

  // ================= FETCH TABLE DATA =================
  const fetchTableData = async () => {
    try {
      setTableLoading(true)
      const res = await axiosInstance.get(GetMedicalDocuments)
      const docs = res.data || []

      const mergedData = docs.map((doc) => {
        const child = childrenList.find((c) => c.id === doc.reg_id)

        return {
          ...doc,
          student: child || {},
        }
      })

      setTableData(mergedData)
    } catch {
      toast.error('Failed to fetch medical records')
    } finally {
      setTableLoading(false)
    }
  }

  // ================= ADD =================
  const handleAdd = () => {
    form.resetFields()
    setIsEdit(false)
    setRecordId(null)
    setShowForm(true)
  }

  // ================= EDIT =================
  const handleEdit = async (record) => {
    try {
      setEditLoading(true)
      const res = await axiosInstance.get(
        `${GetMedicalDocumentsById}/${record.reg_id}`,
      )

      const data = res.data

      form.setFieldsValue({
        childName: record.reg_id,
        bloodGroup: data.blood_group,
        allergies: data.allergies,
        chronicIllness: data.chronic_illness,
        lastCheckupDate: data.date_of_last_health_checkup
          ? dayjs(data.date_of_last_health_checkup)
          : null,

        medicalReport: data.recent_medical_report
          ? [
              {
                uid: '-1',
                name: data.recent_medical_report,
                status: 'done',
                url: IMG_URL + data.recent_medical_report,
              },
            ]
          : [],

        vaccinationCard: data.vaccination_card
          ? [
              {
                uid: '-2',
                name: data.vaccination_card,
                status: 'done',
                url: IMG_URL + data.vaccination_card,
              },
            ]
          : [],

        disabilityCertificate: data.disability_certificate
          ? [
              {
                uid: '-3',
                name: data.disability_certificate,
                status: 'done',
                url: IMG_URL + data.disability_certificate,
              },
            ]
          : [],

        doctorPrescription: data.doctor_prescription_record
          ? [
              {
                uid: '-4',
                name: data.doctor_prescription_record,
                status: 'done',
                url: IMG_URL + data.doctor_prescription_record,
              },
            ]
          : [],

        healthInsuranceCard: data.health_insurance_card
          ? [
              {
                uid: '-5',
                name: data.health_insurance_card,
                status: 'done',
                url: IMG_URL + data.health_insurance_card,
              },
            ]
          : [],
      })

      setIsEdit(true)
      setRecordId(data.reg_id)
      setShowForm(true)
    } catch (error) {
      console.error(error)
    } finally {
      setEditLoading(false)
    }
  }

  // ================= DELETE =================
  const handleDelete = async () => {
    try {
      const res = await axiosInstance.delete(
        `${DeleteMedicalDocuments}/${deleteId}`,
      )
      if (res.data === 'DELETED') {
        toast.success('Deleted successfully')
        setDeleteModal(false)
        fetchTableData()
      } else if (res.data === 'NOTFOUND') {
        toast.warn('Data not found!')
      }
    } catch (err) {
      toast.error('Delete failed')
    }
  }

  // ================= SUBMIT / UPDATE =================
  const handleFinish = async (values) => {
    try {
      setLoading(true)

      const formData = new FormData()
      if (!isEdit) {
        formData.append('reg_id', values.childName)
      }
      formData.append('blood_group', values.bloodGroup || '')
      formData.append('allergies', values.allergies || '')
      formData.append('chronic_illness', values.chronicIllness || '')
      formData.append(
        'date_of_last_health_checkup',
        values.lastCheckupDate
          ? values.lastCheckupDate.format('YYYY-MM-DD')
          : '',
      )

      const appendFile = (field, key) => {
        if (!values[field] || values[field].length === 0) return

        const file = values[field][0]

        if (file.originFileObj) {
          formData.append(key, file.originFileObj)
        } else if (file.url) {
          formData.append(key, file.url.replace(IMG_URL, ''))
        }
      }

      appendFile('medicalReport', 'recent_medical_report')
      appendFile('vaccinationCard', 'vaccination_card')
      appendFile('disabilityCertificate', 'disability_certificate')
      appendFile('doctorPrescription', 'doctor_prescription_record')
      appendFile('healthInsuranceCard', 'health_insurance_card')

      let res

      if (isEdit) {
        res = await axiosInstance.put(
          `${UpdateMedicalDocuments}/${recordId}`,
          formData,
        )

        if (res.data === 'UPDATED') {
          toast.success('Medical Data Updated Successfully!')
          form.resetFields()
          setIsEdit(false)
          setRecordId(null)
          setShowForm(false)
          fetchTableData()
        }
      } else {
        res = await axiosInstance.post(saveMedicalDocuments, formData)

        if (res.data === 'SAVED') {
          toast.success('Medical Data Submitted Successfully!')
          form.resetFields()
          setIsEdit(false)
          setRecordId(null)
          setShowForm(false)
          fetchTableData()
        } else if (res.data === 'ALREADY_EXIST') {
          toast.warn('Data already exist!')
        }
      }
    } catch {
      toast.error('Failed to submit medical data')
    } finally {
      setLoading(false)
    }
  }

  // ================= FILE VALIDATION =================
  const validateFile = (file) => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/pdf',
    ]

    const isAllowedType = allowedTypes.includes(file.type)

    if (!isAllowedType) {
      toast.error('Only JPG, PNG, and PDF files are allowed!')
      return Upload.LIST_IGNORE
    }

    const isLt3M = file.size / 1024 / 1024 < 3

    if (!isLt3M) {
      toast.warn('File must be smaller than 3MB!')
      return Upload.LIST_IGNORE
    }

    return false // prevent auto upload
  }

  // ================= TABLE COLUMNS =================
  const columns = [
    {
      title: 'Sr No',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      title: 'Reg No',
      render: (_, record) => record?.student?.reg_no || '',
    },
    {
      title: 'Student Name',
      render: (_, record) =>
        `${record?.student?.fname || ''} 
         ${record?.student?.mname || ''} 
         ${record?.student?.lname || ''}`,
    },

    {
      title: 'Blood Group',
      dataIndex: 'blood_group',
    },
    ...(showActionColumn
      ? [
          {
            title: 'Action',
            render: (_, record) => (
              <Space>
                {pagePermission.is_view == 1 && (
                  <Button
                    title="View"
                    icon={<EyeOutlined />}
                    onClick={() => handleView(record)}
                  />
                )}
                {pagePermission.is_edit == 1 && (
                  <Button
                    title="Edit"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                  />
                )}
                {pagePermission.is_delete == 1 && (
                  <Button
                    title="Delete"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      setDeleteId(record.reg_id)
                      setDeleteModal(true)
                    }}
                  />
                )}
              </Space>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <AdminHeader />

      <div className="student-form-section container-fluid">
        <div className="student-form-wrapper pb-5">
          <div className="row student-form-title">
            <h3 className="section-title1 text-start mb-3">
              Medical Information & Related Documents
            </h3>
          </div>

          {/* ================= TABLE ================= */}
          {!showForm && (
            <>
              <Row justify="space-between" className="mb-3">
                <Col xs={24} md={10} lg={14}></Col>
                <Col
                  xs={24}
                  md={6}
                  lg={4}
                  className="text-end px-md-2 pb-2 pb-md-0"
                >
                  {pagePermission.is_new == 1 && (
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAdd}
                    >
                      Add
                    </Button>
                  )}
                </Col>

                <Col xs={24} md={6} lg={6}>
                  <Input
                    placeholder="Search by name"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                  />
                </Col>
              </Row>

              <Table
                dataSource={tableData.filter((item) => {
                  const search = searchText.toLowerCase()

                  const fname = item?.student?.fname?.toLowerCase() || ''
                  const lname = item?.student?.lname?.toLowerCase() || ''
                  const regNo = item?.student?.reg_no
                    ? String(item.student.reg_no).toLowerCase()
                    : ''

                  return (
                    fname.includes(search) ||
                    lname.includes(search) ||
                    regNo.includes(search)
                  )
                })}
                columns={columns}
                rowKey="id"
                pagination={{
                  current: currentPage,
                  pageSize: pageSize,
                  onChange: (page, size) => {
                    setCurrentPage(page)
                    setPageSize(size)
                  },
                }}
                bordered
                loading={tableLoading}
                className="shadow rounded custom-table p-0"
                style={{
                  overflowX: 'auto',
                  border: '1px solid #dfdfdf',
                  backgroundColor: 'white',
                }}
              />
            </>
          )}

          {/* ================= FORM ================= */}
          {showForm && (
            <Spin spinning={editLoading}>
              <div className="row student-form-container shadow p-4 rounded bg-white">
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleFinish}
                  autoComplete="off"
                >
                  {/* CHILD DROPDOWN */}
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={12} lg={10}>
                      <Form.Item
                        label="Select Children Name"
                        name="childName"
                        rules={[{ required: true }]}
                      >
                        <Select
                          showSearch
                          disabled={isEdit}
                          placeholder="Search by name / reg no"
                          filterOption={(input, option) => {
                            const child = childrenList.find(
                              (c) => c.id === option.value,
                            )
                            const searchText = `
                            ${child?.fname}
                            ${child?.mname}
                            ${child?.lname}
                            ${child?.reg_no}
                          `.toLowerCase()

                            return searchText.includes(input.toLowerCase())
                          }}
                        >
                          {childrenList.map((child) => (
                            <Option key={child.id} value={child.id}>
                              {child.fname} {child.mname} {child.lname} (Reg No:{' '}
                              {child.reg_no})
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* MEDICAL INFO + DOCUMENTS */}
                  <Row
                    gutter={16}
                    className="mt-3 p-2 border rounded"
                    style={{ background: '#fafafa' }}
                  >
                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label="Blood Group"
                        name="bloodGroup"
                        rules={[{ required: true }]}
                      >
                        <Select placeholder="Select blood group">
                          {[
                            'A+',
                            'A-',
                            'B+',
                            'B-',
                            'O+',
                            'O-',
                            'AB+',
                            'AB-',
                          ].map((bg) => (
                            <Option key={bg} value={bg}>
                              {bg}
                            </Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={8} lg={5}>
                      <Form.Item
                        label="Last Health Checkup"
                        name="lastCheckupDate"
                        rules={[{ required: true }]}
                      >
                        <DatePicker
                          style={{ width: '100%' }}
                          format="DD-MM-YYYY"
                        />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={8} lg={14}></Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Allergies"
                        name="allergies"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Chronic Illness"
                        name="chronicIllness"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    {[
                      { label: 'Medical Report', name: 'medicalReport' },
                      { label: 'Vaccination Card', name: 'vaccinationCard' },
                      {
                        label: 'Disability Certificate',
                        name: 'disabilityCertificate',
                      },
                      {
                        label: 'Doctor Prescription',
                        name: 'doctorPrescription',
                      },
                      {
                        label: 'Health Insurance Card',
                        name: 'healthInsuranceCard',
                      },
                    ].map((item) => (
                      <Col xs={24} md={12} lg={6} key={item.name}>
                        <Form.Item
                          label={item.label}
                          name={item.name}
                          valuePropName="fileList"
                          getValueFromEvent={(e) =>
                            Array.isArray(e) ? e : e?.fileList
                          }
                          rules={[{ required: true }]}
                        >
                          <Upload
                            beforeUpload={validateFile}
                            accept=".jpg,.jpeg,.png,.pdf"
                            maxCount={1}
                            listType="picture"
                            style={{ width: '100%' }}
                          >
                            <Button
                              icon={<UploadOutlined />}
                              style={{ width: '100%' }}
                            >
                              Upload (Max 3MB)
                            </Button>
                          </Upload>
                        </Form.Item>
                      </Col>
                    ))}
                  </Row>

                  <Divider />

                  <Row justify="end">
                    <Space>
                      <Button
                        onClick={() => {
                          form.resetFields()
                          setIsEdit(false)
                          setRecordId(null)
                          setShowForm(false)
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                      >
                        {isEdit ? 'Update' : 'Submit'}
                      </Button>
                    </Space>
                  </Row>
                </Form>
              </div>
            </Spin>
          )}

          {/* ===== DELETE MODAL ===== */}
          <Modal
            title="Confirm Delete"
            open={deleteModal}
            onOk={handleDelete}
            onCancel={() => setDeleteModal(false)}
          >
            Are you sure you want to delete this record?
          </Modal>

          {/* ===== VIEW MODAL ===== */}
          <Modal
            title={
              <div style={{ fontSize: '18px', fontWeight: 600 }}>
                👁 View Medical Details
              </div>
            }
            open={viewModalVisible}
            onCancel={() => setViewModalVisible(false)}
            footer={[
              <Button
                key="close"
                type="primary"
                onClick={() => setViewModalVisible(false)}
              >
                Close
              </Button>,
            ]}
            width={900}
            style={{
              top: 20, // space from top
              paddingBottom: 50, // space from bottom
            }}
            centered
            bodyStyle={{
              maxHeight: '75vh', // control height
              overflowY: 'auto',
              overflowX: 'hidden',
              paddingBottom: 10,
            }}
          >
            {viewData && (
              <div
                style={{
                  backgroundColor: '#f5f7fa',
                  padding: '20px 5px',
                  borderRadius: '8px',
                }}
              >
                {/* Student Name */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Student Name :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.student.fname} {viewData.student.mname}{' '}
                      {viewData.student.lname}
                    </div>
                  </Col>
                </Row>

                {/* Blood Group */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Blood Group :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.blood_group || '-'}
                    </div>
                  </Col>
                </Row>

                {/* Last Checkup Date */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Last Checkup Date :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.date_of_last_health_checkup
                        ? dayjs(viewData.date_of_last_health_checkup).format(
                            'DD-MM-YYYY',
                          )
                        : '-'}
                    </div>
                  </Col>
                </Row>

                {/* Allergies */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Allergies :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">{viewData.allergies || '-'}</div>
                  </Col>
                </Row>

                {/* Chronic Illness */}
                <Row className="mb-3">
                  <Col span={6}>
                    <strong>Chronic Illness :</strong>
                  </Col>
                  <Col span={18}>
                    <div className="view-box">
                      {viewData.chronic_illness || '-'}
                    </div>
                  </Col>
                </Row>

                {/* ===== Files Section ===== */}
                <Divider />

                <Row gutter={16}>
                  {viewData.recent_medical_report && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Medical Report</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.recent_medical_report}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.vaccination_card && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Vaccination Card</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.vaccination_card}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.disability_certificate && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Disability Certificate</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.disability_certificate}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.doctor_prescription_record && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Doctor Prescription</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.doctor_prescription_record}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}

                  {viewData.health_insurance_card && (
                    <Col span={12} className="py-2">
                      <div className="file-card">
                        <strong>Health Insurance Card</strong>
                        <div style={{ marginTop: 10 }}>
                          <a
                            href={IMG_URL + viewData.health_insurance_card}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View File
                          </a>
                        </div>
                      </div>
                    </Col>
                  )}
                </Row>
              </div>
            )}
          </Modal>
        </div>
      </div>

      <AdminFooter />
    </>
  )
}

export default MedicalDocumentsForm
